import {Injectable} from '@angular/core';

import {
  StructuredContent,
  TariffsConfiguration,
  TariffCategory,
  TariffCategoryMapping
} from '../model/structured-content-models';
import {ApiService} from './api.service';
import {TariffOptionConfiguration} from '../model/tariff-option-configuration';
import {FieldConfiguration} from '../model/field-configuration';
import {PricesWrapper} from '../model/prices-wrapper';
import {CalculationResponse} from '../model/calculation-response';


@Injectable({
  providedIn: 'root'
})
export class TariffConfigurationService {

  public static CALCULATION_ID_EMPLOYERS_CONTRIBUTION = 'arbeitgeberanteil';
  public static CALCULATION_ID_DEFAULT_VALUE_INDICATOR = 'benutzeStandardWerte';

  public tariffOptions: Map<string, TariffOptionConfiguration>;
  public prices: Map<string, PricesWrapper>;
  private apiSvc: ApiService;
  private tariffsConfig: TariffsConfiguration;

  constructor(apiSvc: ApiService) {
    this.tariffOptions = new Map<string, TariffOptionConfiguration>();
    this.prices = new Map<string, PricesWrapper>();
    this.apiSvc = apiSvc;
  }

  buildFromStructuredContent(config: StructuredContent): void {

    for (const rli of config.bausteinRowList) {
      for (const li of rli.bausteinList) {

        if (li.tariffOption) {
          const key = li.tariffOption.calculationId;

          const toc = new TariffOptionConfiguration();
          toc.active = li.tariffOption.yesnoswitch.invisible;
          toc.disabled = li.tariffOption.yesnoswitch.disabled;

          if (li.tariffOption.detailselectionfieldList && li.tariffOption.detailselectionfieldList.length > 0) {
            for (const fi of li.tariffOption.detailselectionfieldList) {
              const fieldName: string = fi.dropdownInput ? fi.dropdownInput.fieldName : fi.radioInput.fieldName;

              toc.fields[fieldName] = new FieldConfiguration();

            }
          }
          this.tariffOptions.set(key, toc);
          this.prices.set(key, new PricesWrapper());
        }
      }
    }

    this.addEmployersContribution();
    this.addDefaultValueIndicator();
  }

  setTariffsConfig(tariffsConfig: TariffsConfiguration) {
    this.tariffsConfig = tariffsConfig;
  }

  getTariffOptionById(calculationId: string): TariffOptionConfiguration {
    return this.tariffOptions.get(calculationId);
  }

  getPricesById(calculationId: string): PricesWrapper {
    return this.prices.get(calculationId);
  }

  calculate(sessionId: string, mandantId: string): Promise<CalculationResponse> {
    return this.apiSvc.calculateTariff(this.tariffOptions, this.prices, sessionId, mandantId);
  }

  addEmployersContribution() {

    if(!this.apiSvc.isUsingEmployersContribution()) { return; }

    const toc = new TariffOptionConfiguration();
    const pricesWrapper = new PricesWrapper();
    pricesWrapper.priceUkv = 0;
    pricesWrapper.priceBk = 0;
    this.tariffOptions.set(TariffConfigurationService.CALCULATION_ID_EMPLOYERS_CONTRIBUTION, toc);
    this.prices.set(TariffConfigurationService.CALCULATION_ID_EMPLOYERS_CONTRIBUTION, pricesWrapper);
  }

  addDefaultValueIndicator() {
    if(!this.apiSvc.isUsingDefaultProfileValues()) { return; }
    this.tariffOptions.set(TariffConfigurationService.CALCULATION_ID_DEFAULT_VALUE_INDICATOR, new TariffOptionConfiguration());
  }

  postProcess() {
    const categorizedEmployersContributions: any = {};
    let totalEmployersContributionUkv = 0;
    let totalEmployersContributionBk = 0;

    if(!this.apiSvc.isUsingEmployersContribution()) { return; }

    const tocEmployersContribution: TariffOptionConfiguration = this.tariffOptions.get(
      TariffConfigurationService.CALCULATION_ID_EMPLOYERS_CONTRIBUTION
    );

    if (tocEmployersContribution.active) {

      this.tariffsConfig.tariffCategories.forEach((value: TariffCategory) => {
        const clusteredTariffs = this.getTariffsByCategoryID(value.id);
        const sumUkv = this.sumUpTariffPricesByCluster(clusteredTariffs, 'ukv');
        const sumBk = this.sumUpTariffPricesByCluster(clusteredTariffs, 'bk');
        categorizedEmployersContributions[value.id] = {
          employersContributionUkv: this.calculateEmployersContribution(sumUkv, value.id),
          employersContributionBk: this.calculateEmployersContribution(sumBk, value.id)
        };
      });

      this.tariffsConfig.tariffCategories.forEach(element => {
        totalEmployersContributionUkv += categorizedEmployersContributions[element.id].employersContributionUkv;
      });

      this.tariffsConfig.tariffCategories.forEach(element => {
        totalEmployersContributionBk += categorizedEmployersContributions[element.id].employersContributionBk;
      });
    }

    this.prices.get(TariffConfigurationService.CALCULATION_ID_EMPLOYERS_CONTRIBUTION).priceUkv = totalEmployersContributionUkv;
    this.prices.get(TariffConfigurationService.CALCULATION_ID_EMPLOYERS_CONTRIBUTION).priceBk = totalEmployersContributionBk;
  }

  calculateEmployersContribution(sumOfPrices: number, categoryId: string): number {
    const tariffCategory = this.tariffsConfig.tariffCategories.find((value: TariffCategory) => value.id === categoryId);
    const factorizedSumOfPrices = tariffCategory.factor * sumOfPrices;
    return factorizedSumOfPrices > tariffCategory.maxValue ? tariffCategory.maxValue : factorizedSumOfPrices;
  }

  sumUpTariffPricesByCluster(cluster: TariffCategoryMapping[], context: string): number {
    let sum = 0;
    cluster.forEach((value: TariffCategoryMapping) => {
      if (this.prices.has(value.tariffId) && this.tariffOptions.get(value.tariffId).active) {
        if (context === 'ukv') {
          sum += this.prices.get(value.tariffId).priceUkv;
        }
        if (context === 'bk') {
          sum += this.prices.get(value.tariffId).priceBk;
        }
      }
    });
    return sum;
  }

  getTariffsByCategoryID(categoryID: string): TariffCategoryMapping[] {
    return this.tariffsConfig.tariffCategoryMappings.filter((item) => {
      return item.categoryId === categoryID;
    });
  }
}
